import React from 'react';
import { IndexRoute, Redirect, Route } from 'react-router';

import App from 'containers/App';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import MelodyCardProduct from 'components/common/melodyCard/MelodyCardProduct';
import SearchMenu from 'components/search/SearchMenu';
import StandardSearch from 'containers/StandardSearch';
import { childPathFactory, shouldAccountIgnoreScrollToTop, shouldCheckoutIgnoreScrollToTop, shouldPdpIgnoreScrollToTop, shouldSearchIgnoreScrollToTop } from 'helpers/RouteUtils';
import { logError } from 'middleware/logger';

const MOUNT_PATHS = ['/marty', '/'];

const loadRoute = cb => module => cb(null, module.default);

// TODO: what can we do here? This really should never, ever happen....
const errorLoading = err => logError('Dynamic page loading failed', err);
const lazyHandler = (promise, cb) => promise.then(loadRoute(cb)).catch(errorLoading);

// See https://webpack.js.org/api/module-methods/#magic-comments
// These webpack chunk names must either match the component class name, or the class itself must define `static webpackChunkName`
// for CSS splitting to work without users seeing a FOUC.  These chunksNames are then referenced via the produced webpackStats object.
const LazyAccount = (location, cb) => lazyHandler(import(/* webpackChunkName: "Account" */ 'containers/account/Account'), cb);
const LazyAddPayment = (location, cb) => lazyHandler(import(/* webpackChunkName: "AddPayment" */ 'containers/account/AddPayment'), cb);
const LazyAddReview = (location, cb) => lazyHandler(import(/* webpackChunkName: "AddReview" */ 'containers/AddReview'), cb);
const LazyAddShipping = (location, cb) => lazyHandler(import(/* webpackChunkName: "AddShipping" */ 'containers/account/AddShipping'), cb);
const LazyAsinStockIdProductDetail = (location, cb) => lazyHandler(import(/* webpackChunkName: "AsinStockIdProductDetail" */ 'containers/AsinStockIdProductDetail'), cb);
const LazyAsinAddReview = (location, cb) => lazyHandler(import(/* webpackChunkName: "AsinAddReview" */ 'containers/AsinAddReview'), cb);
const LazyTaxonomyBrand = (location, cb) => lazyHandler(import(/* webpackChunkName: "BrandPage" */ 'containers/TaxonomyBrandPage'), cb);
const LazyCart = (location, cb) => lazyHandler(import(/* webpackChunkName: "Cart" */ 'containers/Cart'), cb);
const LazyCheckout = (location, cb) => lazyHandler(import(/* webpackChunkName: "Checkout" */ 'containers/checkout/Checkout'), cb);
const LazyEditPayment = (location, cb) => lazyHandler(import(/* webpackChunkName: "EditPayment" */ 'containers/account/EditPayment'), cb);
const LazyEditShipping = (location, cb) => lazyHandler(import(/* webpackChunkName: "EditShipping" */ 'containers/account/EditShipping'), cb);
const LazyHmdSurvey = (location, cb) => lazyHandler(import(/* webpackChunkName: "HmdSurvey" */ 'containers/HmdSurvey'), cb);
const LazySubscriptions = (location, cb) => lazyHandler(import(/* webpackChunkName: "Subscriptions" */ 'containers/Subscriptions'), cb);
const LazyEGiftCard = (location, cb) => lazyHandler(import(/* webpackChunkName: "EGiftCard" */ 'containers/EGiftCard'), cb);
const LazyFavorites = (location, cb) => lazyHandler(import(/* webpackChunkName: "Favorites" */ 'containers/account/FavoritesPicker'), cb);
const LazyCollection = (location, cb) => lazyHandler(import(/* webpackChunkName: "Collection" */ 'containers/account/Collection'), cb);
const LazyLanding = (location, cb) => lazyHandler(import(/* webpackChunkName: "Landing" */ 'containers/Landing'), cb);
const LazyOrder = (location, cb) => lazyHandler(import(/* webpackChunkName: "Order" */ 'containers/account/Order'), cb);
const LazyCancelOrder = (location, cb) => lazyHandler(import(/* webpackChunkName: "CancelOrder" */ 'containers/account/CancelOrder'), cb);
const LazyOrderHistory = (location, cb) => lazyHandler(import(/* webpackChunkName: "OrderHistory" */ 'containers/account/OrderHistory'), cb);
const LazyOrderConfirmation = (location, cb) => lazyHandler(import(/* webpackChunkName: "OrderConfirmation" */ 'containers/checkout/OrderConfirmation'), cb);
const LazyPayments = (location, cb) => lazyHandler(import(/* webpackChunkName: "Payments" */ 'containers/account/Payments'), cb);
const LazyProductImage = (location, cb) => lazyHandler(import(/* webpackChunkName: "ProductImage" */ 'containers/ProductImage'), cb);
const LazyProductDetail = (location, cb) => lazyHandler(import(/* webpackChunkName: "ProductDetail" */ 'containers/ProductDetail'), cb);
const LazyProductReviews = (location, cb) => lazyHandler(import(/* webpackChunkName: "ProductReviews" */ 'containers/ProductReviews'), cb);
const LazyReturns = (location, cb) => lazyHandler(import(/* webpackChunkName: "Returns" */ 'containers/account/Returns'), cb);
const LazyReturnAll = (location, cb) => lazyHandler(import(/* webpackChunkName: "ReturnAll" */ 'containers/account/ReturnAll'), cb);
const LazyReturnLabel = (location, cb) => lazyHandler(import(/* webpackChunkName: "ReturnLabel" */ 'containers/account/ReturnLabel'), cb);
const LazyRewardsDashboard = (location, cb) => lazyHandler(import(/* webpackChunkName: "RewardsDashboard" */ 'containers/account/RewardsDashboardPicker'), cb);

const LazyRouteNotFound = (location, cb) => lazyHandler(import(/* webpackChunkName: "RouteNotFound" */ 'containers/RouteNotFound'), cb);
const LazySearch = (location, cb) => lazyHandler(import(/* webpackChunkName: "StandardSearch" */ 'containers/StandardSearch'), cb);
const LazyShipping = (location, cb) => lazyHandler(import(/* webpackChunkName: "Shipping" */ 'containers/account/Shipping'), cb);
const LazyShipmentTracking = (location, cb) => lazyHandler(import(/* webpackChunkName: "ShipmentTracking" */ 'containers/account/ShipmentTracking'), cb);
const LazySymphonyPreview = (location, cb) => lazyHandler(import(/* webpackChunkName: "SymphonyPreview" */ 'containers/SymphonyPreview'), cb);
const LazyCreateLabel = (location, cb) => lazyHandler(import(/* webpackChunkName: "CreateLabel" */ 'containers/CreateLabel'), cb);
const LazyBrokenPage = (location, cb) => lazyHandler(import(/* webpackChunkName: "BrokenPage" */ 'components/error/BrokenPage'), cb);
const LazyWildCard = (location, cb) => lazyHandler(import(/* webpackChunkName: "WildCard" */ 'containers/WildCard'), cb);
const LazyDealsForYou = (location, cb) => lazyHandler(import(/* webpackChunkName: "DealsForYou" */ 'containers/DealsForYou'), cb);

const makeAppRoutes = pathPrefix => {
  const makePath = childPathFactory(pathPrefix);
  return (
    <Route path={pathPrefix} component={App} key={`app-${pathPrefix}`}>
      <IndexRoute getComponent={LazyLanding}/>

      <Route path={makePath('/deals-for-you')} getComponent={LazyDealsForYou} />

      <Route
        path={makePath('/(filters/)search*')}
        getComponent={LazySearch}
        suppressScrollOnRouteChange={shouldSearchIgnoreScrollToTop}
        ProductComponent={MelodyCardProduct}
        SearchMenu={SearchMenu}/>
      <Route
        path={makePath('/filters/**/**.zso')}
        getComponent={LazySearch}
        suppressScrollOnRouteChange={shouldSearchIgnoreScrollToTop}
        ProductComponent={MelodyCardProduct}
        SearchMenu={SearchMenu}/>
      <Route path={makePath('/filters/**.zso')} getComponent={LazyRouteNotFound} status="404"></Route>
      <Route
        path={makePath('/**/**.zso')}
        getComponent={LazySearch}
        suppressScrollOnRouteChange={shouldSearchIgnoreScrollToTop}
        ProductComponent={MelodyCardProduct}
        SearchMenu={SearchMenu}/>
      <Route path={makePath('/cart')} getComponent={LazyCart}></Route>

      {/* Checkout */}
      <Route path={makePath('/checkout')} getComponent={LazyCheckout} suppressScrollOnRouteChange={false}></Route>
      <Route path={makePath('/checkout/spc')} getComponent={LazyCheckout} suppressScrollOnRouteChange={shouldCheckoutIgnoreScrollToTop}></Route>
      <Route path={makePath('/checkout/initiate')} getComponent={LazyCheckout} suppressScrollOnRouteChange={false}></Route>
      <Route path={makePath('/checkout/address')} getComponent={LazyCheckout} suppressScrollOnRouteChange={shouldCheckoutIgnoreScrollToTop}></Route>
      <Route path={makePath('/checkout/address/new')} getComponent={LazyCheckout} suppressScrollOnRouteChange={shouldCheckoutIgnoreScrollToTop}></Route>
      <Route path={makePath('/checkout/address/edit')} getComponent={LazyCheckout} suppressScrollOnRouteChange={shouldCheckoutIgnoreScrollToTop}></Route>
      <Route path={makePath('/checkout/payment')} getComponent={LazyCheckout} suppressScrollOnRouteChange={shouldCheckoutIgnoreScrollToTop}></Route>
      <Route path={makePath('/checkout/payment/new')} getComponent={LazyCheckout} suppressScrollOnRouteChange={shouldCheckoutIgnoreScrollToTop}></Route>
      <Route path={makePath('/checkout/shipoption')} getComponent={LazyCheckout} suppressScrollOnRouteChange={shouldCheckoutIgnoreScrollToTop}></Route>
      <Route path={makePath('/checkout/billing')} getComponent={LazyCheckout} suppressScrollOnRouteChange={shouldCheckoutIgnoreScrollToTop}></Route>
      <Route path={makePath('/checkout/billing/new')} getComponent={LazyCheckout} suppressScrollOnRouteChange={shouldCheckoutIgnoreScrollToTop}></Route>
      <Route path={makePath('/checkout/billing/edit')} getComponent={LazyCheckout} suppressScrollOnRouteChange={shouldCheckoutIgnoreScrollToTop}></Route>
      <Route path={makePath('/checkout/giftoptions')} getComponent={LazyCheckout} suppressScrollOnRouteChange={shouldCheckoutIgnoreScrollToTop}></Route>

      {/* Order Confirmation */}
      <Route path={makePath('/confirmation/:purchaseId')} getComponent={LazyOrderConfirmation}></Route>

      <Route path={makePath('/p/asin/:asin')} getComponent={LazyAsinStockIdProductDetail}/>
      <Route path={makePath('/product/stockId/:stockId')} getComponent={LazyAsinStockIdProductDetail}/>
      <Route path={makePath('/p/:seoName/product/:productId(/color/:colorId)')} getComponent={LazyProductDetail} suppressScrollOnRouteChange={shouldPdpIgnoreScrollToTop}/>
      <Route path={makePath('/p/product/:productId(/color/:colorId)')} getComponent={LazyProductDetail} suppressScrollOnRouteChange={shouldPdpIgnoreScrollToTop}/>
      <Route path={makePath('/product/:productId(/color/:colorId)')} getComponent={LazyProductDetail} suppressScrollOnRouteChange={shouldPdpIgnoreScrollToTop}/>
      <Route path={makePath('/product/review/:productId(/page/:reviewsPage)(/start/:reviewsStart)(/orderBy/:orderBy)(/review/:reviewId)')} getComponent={LazyProductReviews}/>
      <Route path={makePath('/product/review/(:p/)add/:productId(/color/:colorId)')} getComponent={LazyAddReview}/>
      <Route path={makePath('/createReview/asin/:asin')} getComponent={LazyAsinAddReview}/>
      {/* My Account Start */}
      <Route path={makePath('/account')} getComponent={LazyAccount} suppressScrollOnRouteChange={shouldAccountIgnoreScrollToTop}/>
      <Route path={makePath('/account/favorites')} getComponent={LazyFavorites}/>
      <Route path={makePath('/account/favorites/:id')} getComponent={LazyCollection}/>
      <Route path={makePath('/orders')} getComponent={LazyOrderHistory} suppressScrollOnRouteChange={shouldAccountIgnoreScrollToTop}/>
      <Route path={makePath('/rewards/dashboard')} getComponent={LazyRewardsDashboard}/>
      <Route path={makePath('/vip/dashboard')} getComponent={LazyRewardsDashboard}/>
      <Redirect from={makePath('/rewards/optin')} to={makePath('/rewards/dashboard')}/>
      <Redirect from={makePath('/rewards/confirmed')} to={makePath('/rewards/dashboard')}/>
      <Redirect from={makePath('/vip/optin')} to={makePath('/vip/dashboard')}/>
      <Redirect from={makePath('/vip/confirmed')} to={makePath('/vip/dashboard')}/>

      <Route path={makePath('/orders/:orderId')} getComponent={LazyOrder}/>
      <Route path={makePath('/orders/:orderId/cancel')} getComponent={LazyCancelOrder}/>
      <Route path={makePath('/shipments/:orderId/:shipmentId')} getComponent={LazyShipmentTracking}/>
      <Route path={makePath('/payments')} getComponent={LazyPayments}/>
      <Route path={makePath('/payments/new')} getComponent={LazyAddPayment}/>
      <Route path={makePath('/payments/:paymentId/edit')} getComponent={LazyEditPayment}/>
      <Route path={makePath('/addresses')} getComponent={LazyShipping}/>
      <Route path={makePath('/addresses/new')} getComponent={LazyAddShipping}/>
      <Route path={makePath('/addresses/:addressId/edit')} getComponent={LazyEditShipping}/>
      <Route path={makePath('/returns')} getComponent={LazyReturnAll}/>
      <Route path={makePath('/return')} getComponent={LazyReturns}/>
      <Route path={makePath('/returnInitiate')} getComponent={LazyReturns}/>
      <Route path={makePath('/returnLabel')} getComponent={LazyReturnLabel}/>
      <Route path={makePath('/subscriptions')} getComponent={LazySubscriptions}/>
      <Route path={makePath('/create-label')} getComponent={LazyCreateLabel} />
      {/* My Account End */}

      <Route path={makePath('/c/survey')} getComponent={LazyHmdSurvey}/>
      <Route path={makePath('/survey')} getComponent={LazyHmdSurvey}/>
      <Route path={makePath('/c/:pageName')} getComponent={LazyLanding}/>
      <Route path={makePath('/brand/:brandId')} getComponent={LazyTaxonomyBrand}/>
      <Route path={makePath('/b/:brandName/brand/:brandId')} getComponent={LazyTaxonomyBrand}/>
      <Route path={makePath('/egiftcard')} getComponent={LazyEGiftCard}/>
      <Route path={makePath('/gift-certificate')} getComponent={LazyEGiftCard}/>
      <Route path={makePath('/product/')} getComponent={LazyRouteNotFound}/>
      <Route path={makePath('/p/')} getComponent={LazyRouteNotFound}/>
      <Route path={makePath('/error/:statusToSend')} getComponent={LazyRouteNotFound} />
      <Route path={makePath('/s/e/c/r/e/t/r/o/u/t/e')} getComponent={LazyBrokenPage} />

      {/* Prevent these requests from hitting the wildcard container. These should be 404ing since they are not valid seo term urls */}
      <Route path={makePath('/*_*')} getComponent={LazyRouteNotFound}/>
      <Route path={makePath('/*.*')} getComponent={LazyRouteNotFound}/>

      <Route
        path={makePath('/:seoName')}
        getComponent={LazyWildCard}
        SearchComponent={StandardSearch}
        suppressScrollOnRouteChange={shouldSearchIgnoreScrollToTop}
        ProductComponent={MelodyCardProduct}
        SearchMenu={SearchMenu}/>
      {/* If none of our known routes match, show a nice 404 page.
          This must be the last route under the App component or else it'll match valid routes.
      */}
      <Route path="*" getComponent={LazyRouteNotFound} />
    </Route>
  );
};

const makeNoAppRoutes = pathPrefix => {
  const makePath = childPathFactory(pathPrefix);
  return (
    <Route key={`app-${pathPrefix}`}>
      <Route path={makePath('/i/:productId(/color/:colorId)/style/:styleId/:index')} getComponent={LazyProductImage} key={`img-${pathPrefix}`}/>
      <Route path={makePath('/c/symphonypreview')} getComponent={LazySymphonyPreview}/>
      <Route path={makePath('/contentPreview')} getComponent={LazySymphonyPreview}/>
    </Route>
  );
};

export default function makeRoutes() {
  return (
    <Route>
      <SiteAwareMetadata>
        {MOUNT_PATHS.map(makeNoAppRoutes)}
        {MOUNT_PATHS.map(path => makeAppRoutes(path))}
      </SiteAwareMetadata>
    </Route>
  );
}
